import type { GameContent } from '../../data/types';
import { t, tn } from '../../shared/i18n';
import { setOf } from '../../sim/lineup';
import type { SimState } from '../../sim/state';
import { el } from '../dom';
import { formatDuration } from '../format';
import type { EndPart, Ending } from '../end-parts';
import './festival.css';

export interface StageLine {
  name: string;
  time: string;
  held: boolean;
}

export function stageLines(state: SimState, content: GameContent): StageLine[] {
  return (state.festival?.scenes ?? []).map((scene) => ({
    name: t(setOf(content, scene.setId).name),
    time: formatDuration((scene.toTick ?? state.tick) - scene.fromTick),
    held: scene.toTick !== undefined,
  }));
}

export function festivalEnding(state: SimState, lines: readonly StageLine[]): Ending {
  if (state.status === 'won') {
    return {
      title: t('Le festival a tenu'),
      text: t('Toutes les scènes, tous les sunrises : le sound system a tenu le festival entier.'),
    };
  }
  const held = lines.filter((line) => line.held).length;
  return {
    title: state.core.hp <= 0 ? t('La musique s’arrête') : t('Plus personne debout'),
    text: tn(
      held,
      '{count} scène tenue, puis la musique s’est arrêtée sur {stage}.',
      '{count} scènes tenues, puis la musique s’est arrêtée sur {stage}.',
      { count: held, stage: lines.at(-1)?.name ?? '' },
    ),
  };
}

export function festivalEnd(state: SimState, content: GameContent | null): EndPart | null {
  if (state.festival === undefined || content === null) {
    return null;
  }
  const lines = stageLines(state, content);
  const list = el('ul', 'ui-fest-end');
  list.setAttribute('aria-label', t('Les scènes du festival'));
  list.append(
    ...lines.map((line) => {
      const item = el('li', 'ui-fest-end__stage');
      item.dataset.held = String(line.held);
      item.append(
        el('span', 'ui-fest-end__name', line.name),
        el('span', 'ui-fest-end__time', line.time),
        el('span', 'ui-fest-end__state', line.held ? t('tenue') : t('la musique s’est arrêtée')),
      );
      return item;
    }),
  );
  return { ending: festivalEnding(state, lines), details: list };
}
